/**
 * layoutGroups — P-GROUP Phase A: the PRE-PAGINATION FRAGMENTATION CONTRACT
 * (docs/architecture/pagination-grouping-plan.md, 2026-07-03).
 *
 * ── WHY ──────────────────────────────────────────────────────────────────────
 * The paged engine used to flow raw DOM into pagedjs and then PATCH bad breaks
 * after the fact (deOrphanHeadings MOVE, orphan break-before collector, prune,
 * dedupe). Reactive passes cannot win — every content shape breaks in a new
 * way (the 2026-07-03 Redhead PDF: a bare "05" numeral stranded at a page foot,
 * the GRID QUEUE callout duplicated across two pages, RAIL ACCESS /
 * TRANSMISSION ACCESS sub-heads stranded, a datasheet clipped mid-row). The
 * durable fix is a contract EMITTED AT RENDER TIME by the section components:
 * every fragile block carries `data-layout-group="<kind>"`, and ONE deck CSS
 * block (injected into the pagedjs Previewer by
 * `PaginatedReportView.buildPagedStylesheet`) gives each kind its fragmentation
 * behaviour. Because the attribute rides the report DOM itself, every report
 * type and BOTH surfaces (view deck + print deck — one DOM path) inherit it.
 *
 * ── THE KINDS ────────────────────────────────────────────────────────────────
 *   opener     — the editorial section opener (numeral + H2 + rule +
 *                standfirst): ONE atom. Kills the stranded-numeral class.
 *   head-body  — a small-caps sub-head PAIRED with its FIRST following block:
 *                break allowed after the pair, never inside it. Kills the
 *                RAIL ACCESS / RECOMMENDATIONS strand class.
 *   callout    — an insight / queue / verdict box: atomic. Kills the
 *                fragmented-and-duplicated callout class.
 *   row-group  — a datasheet / ledger CONTAINER: explicitly FRAGMENTABLE
 *                (break-inside auto + plain block flow), so a tall datasheet
 *                flows across pages instead of clipping at the sheet edge.
 *   row        — one datasheet ROW (a key·leader·value line or a two-up pair
 *                of them): atomic. Together with `row-group` this kills the
 *                clipped-mid-row datasheet class.
 *   exhibit    — a figure/chart + caption + explainer: atomic. Taller-than-
 *                page exhibits are handled by the EXISTING scale-to-fit pass
 *                (`scaleOversizedSpanExhibits`, legibility floor 0.8 ≈ the
 *                plan's "atomic up to ~80% page height" cap) — reused, not
 *                rebuilt.
 *
 * ── PAGEDJS FACTS THE CONTRACT IS BUILT ON (verified 0.4.3 / 0.5.0-beta.2) ──
 *   • `break-inside: avoid` IS honoured reliably → the atomicity mechanism.
 *   • `break-after: avoid` is IGNORED → never relied on here (it remains in
 *     the shared density CSS for the Chromium `@media print` fallback only).
 *   • An avoid-inside box TALLER THAN ONE PAGE overflows and CLIPS (content
 *     loss) → every `avoid` kind here must be size-bounded by construction
 *     (opener/callout/row/head-body pair), or owned by the scale-to-fit pass
 *     (exhibit). Unbounded containers get `row-group` (fragmentable), never
 *     `avoid`.
 *
 * The existing post-passes (deOrphanHeadings, the orphan break-before
 * collector, prune, dedupe, tail-packer) are UNCHANGED — they become
 * defense-in-depth backstops behind this contract.
 */
import React from 'react';

/** The shared marker attribute. */
export const LAYOUT_GROUP_ATTR = 'data-layout-group';

export type LayoutGroupKind =
  | 'opener'
  | 'head-body'
  | 'callout'
  | 'row-group'
  | 'row'
  | 'exhibit';

/** JSX spread helper: `<div {...layoutGroupProps('callout')}>`. */
export function layoutGroupProps(
  kind: LayoutGroupKind,
): { [LAYOUT_GROUP_ATTR]: LayoutGroupKind } {
  return { [LAYOUT_GROUP_ATTR]: kind };
}

/**
 * The deck stylesheet block for the layout-group contract.
 *
 * Injected ONLY into the pagedjs Previewer (buildPagedStylesheet) — it never
 * touches the global document, the continuous viewer, or the DOCX/PPTX
 * exports, so the selectors are deliberately UNSCOPED (pagedjs relocates
 * sections out of their ancestors during fragmentation, so ancestor-scoped
 * selectors are unreliable there — see the deck-stylesheet comments in
 * PaginatedReportView).
 *
 * `!important` so the contract wins over the hoisted report-root inline
 * styles (e.g. the 2-col `[data-flow-card] { break-inside: avoid }` island
 * rule that made the Site-Specific Research block clip) at any specificity.
 */
export function reportLayoutGroupCss(): string {
  return (
    // ── ATOMIC kinds (break-inside: avoid) — the bounded CLASSIC exhibits only:
    // callout / row / exhibit. These are the same box family the deck has
    // always kept atomic (charts, tables, KPI rails), each far below one page.
    //
    // `opener` and `head-body` are DELIBERATELY NOT in this list (measured
    // live): pagedjs 0.4.3's overflow walker, on an avoid-inside box that
    // STRADDLES the page end, can SKIP the subtree and resume PAST it — the
    // engine's historic "silently dropped section" class. The environmental
    // gate cell reproduced it: an avoid-inside head-body pair straddling p5's
    // foot lost its BODY from the deck entirely (content loss — strictly worse
    // than any strand). The head-binding those two kinds need is expressed via
    // the SAFE `break-before: avoid` child binding below instead.
    ` [data-layout-group="callout"],` +
    ` [data-layout-group="row"],` +
    ` [data-layout-group="exhibit"] {` +
    ` break-inside: avoid !important;` +
    ` page-break-inside: avoid !important;` +
    ` -webkit-column-break-inside: avoid !important; }` +
    // ── KEEP-WITH-NEXT binding (all five kinds) — the ONE keep-together
    // mechanism pagedjs 0.4.3 honours at the element-walk level: its Breaks
    // handler stamps `break-before: avoid` as `data-break-before="avoid"`, and
    // `findOverflow` then selects the node BEFORE such a node (`breakAvoid →
    // nodeBefore`) when it starts past the page end — so a group child that
    // would open a page drags its predecessor (the head/label/numeral) along,
    // and the pair moves TOGETHER. When a child merely STRADDLES the boundary
    // the text splits naturally — the head keeps the body's OPENING LINES
    // beside it, which satisfies the head-body contract without the
    // avoid-inside drop hazard. (`break-after: avoid` remains useless — the
    // chunker never consults it — so the binding rides break-before only.)
    ` [data-layout-group="opener"] > * + *,` +
    ` [data-layout-group="head-body"] > * + *,` +
    ` [data-layout-group="callout"] > * + *,` +
    ` [data-layout-group="row"] > * + *,` +
    ` [data-layout-group="exhibit"] > * + * {` +
    ` break-before: avoid !important; }` +
    // Fragmentable container kind — a datasheet/ledger container must FLOW
    // across pages (rows are the atoms). `display: block` guarantees pagedjs
    // can fragment it (it cannot fragment flex/grid/multicol containers), and
    // the explicit `break-inside: auto` defeats any inherited container-level
    // atomicity (the [data-flow-card] island rule, [data-report-block]
    // treatments, blanket exhibit rules).
    ` [data-layout-group="row-group"] {` +
    ` break-inside: auto !important;` +
    ` page-break-inside: auto !important;` +
    ` -webkit-column-break-inside: auto !important;` +
    ` display: block !important; }`
  );
}

/** Inline-level DOM tags: a child run made only of these (plus text) is ONE
 *  flowing prose run — splitting it into two blocks would tear a sentence. */
const INLINE_TAGS = new Set([
  'a', 'abbr', 'b', 'bdi', 'bdo', 'br', 'button', 'cite', 'code', 'data',
  'dfn', 'em', 'i', 'img', 'kbd', 'label', 'mark', 'q', 's', 'samp', 'small',
  'span', 'strong', 'sub', 'sup', 'svg', 'time', 'u', 'var', 'wbr',
]);

/** DOM tags whose height is bounded in practice (a paragraph-scale block) —
 *  safe to bind whole to a preceding sub-head even when it is the ONLY body
 *  block. Unbounded containers (div/ul/ol/table/…) are NOT here: pairing an
 *  unbounded lone block could produce an avoid-inside box taller than a page,
 *  which pagedjs CLIPS (content loss — worse than the strand it prevents). */
const PARAGRAPH_LIKE_TAGS = new Set(['p', 'blockquote', 'figcaption']);

function isBlockNode(node: React.ReactNode): boolean {
  if (!React.isValidElement(node)) return false; // strings / numbers → inline text
  const t = node.type;
  if (typeof t === 'string') return !INLINE_TAGS.has(t.toLowerCase());
  return true; // a component — assume block-level
}

function isParagraphLikeNode(node: React.ReactNode): boolean {
  return (
    React.isValidElement(node) &&
    typeof node.type === 'string' &&
    PARAGRAPH_LIKE_TAGS.has(node.type.toLowerCase())
  );
}

export interface HeadBodyPartition {
  /**
   * The body content to bind INTO the `head-body` atom with the sub-head.
   * Empty ⇒ no safe pairing exists (the caller renders the head + body
   * exactly as before; the reactive backstops own that residual).
   */
  lead: React.ReactNode[];
  /** The remaining body content, rendered OUTSIDE the atom (free to flow). */
  rest: React.ReactNode[];
}

/**
 * Partition a sub-section's body children into the `lead` block(s) that pair
 * with the sub-head inside ONE `head-body` atom and the `rest` that flows
 * freely after it.
 *
 * PURE + conservative — the rules exist to keep every emitted atom
 * SIZE-BOUNDED (pagedjs clips avoid-inside boxes taller than one page):
 *   1. no renderable children → nothing to pair (`lead: []`).
 *   2. an all-inline run (text + [Ref N] pills — the `renderCitedText(...)`
 *      shape behind RAIL ACCESS / TRANSMISSION ACCESS) → the WHOLE run is the
 *      lead: it is one paragraph of prose, bounded in practice, and cannot be
 *      split without tearing a sentence.
 *   3. first child is a block AND a second child exists → pair the FIRST
 *      block only (the plan's "sub-head + its first following block"); the
 *      rest flows.
 *   4. a LONE paragraph-like block (`p` / `blockquote` / `figcaption`) → pair
 *      it whole (bounded).
 *   5. anything else (a lone component / div / list — height unknown) → NO
 *      pairing; the head renders un-grouped and the existing backstops apply.
 */
export function partitionHeadBodyLead(children: React.ReactNode): HeadBodyPartition {
  const kids = React.Children.toArray(children).filter((k) => {
    if (k == null || typeof k === 'boolean') return false;
    if (typeof k === 'string' && k.trim() === '') return false;
    return true;
  });
  if (kids.length === 0) return { lead: [], rest: [] };

  const hasBlock = kids.some(isBlockNode);
  if (!hasBlock) return { lead: kids, rest: [] }; // one inline prose run

  if (isBlockNode(kids[0]) && kids.length >= 2) {
    return { lead: [kids[0]], rest: kids.slice(1) };
  }
  if (kids.length === 1 && isParagraphLikeNode(kids[0])) {
    return { lead: kids, rest: [] };
  }
  return { lead: [], rest: kids };
}

/** Chunk an array into consecutive pairs (the two-up datasheet ledger rows).
 *  The final chunk carries 1 item when the length is odd. */
export function chunkPairs<T>(items: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += 2) {
    out.push(items.slice(i, i + 2) as T[]);
  }
  return out;
}
